import { Link } from "react-router-dom";
import { BookOpen, ChevronRight, MapPin, MapPinned, TriangleAlert, Users } from "lucide-react";
import { Card, CardContent } from "../components/ui/card";

/**
 * `/admin` — the shared admin area's landing page (Andreas, 2026-08-20:
 * "one /admin area with sections"):
 *
 *   /admin
 *   ├── Guides   <- issue #219
 *   ├── Users    <- issue #226
 *   ├── Grounds  <- issue #132
 *   ├── Venues   <- issue #166 (Martin's Q4 decision)
 *   └── Errors   <- issue #1191 (the failed-request record)
 *
 * #219 left this page with ONE tile rather than a "Users — coming soon"
 * placeholder, on the rule that this page shows what exists and not what's
 * planned (PR #191 §10 Q2, the same reason the not-yet-imported guide
 * categories are hidden outright). #226 is that prediction paying off: the
 * Users section arrived as an entry in `SECTIONS` below plus its route, and
 * this page needed no layout or route change of its own — and #132's
 * Grounds section is the same again, a third entry and nothing else.
 */
const SECTIONS = [
  {
    to: "/admin/guides",
    label: "Guides",
    description: "Review imported coaching guides and control what's published.",
    icon: BookOpen,
    testId: "admin-section-guides",
  },
  {
    to: "/admin/users",
    label: "Users",
    description: "Create player and coach accounts, and re-issue lost passwords.",
    icon: Users,
    testId: "admin-section-users",
  },
  {
    to: "/admin/grounds",
    label: "Grounds",
    description: "Review pairs of public grounds that look like the same field recorded twice.",
    icon: MapPin,
    testId: "admin-section-grounds",
  },
  // Issue #166, Martin's Q4 decision: the management console beside the
  // report — edit and archive venue records, manage surfaces, read the
  // audit trail (Q5: admin-only).
  {
    to: "/admin/venues",
    label: "Venues",
    description: "Edit venue records, archive retired ones, and read each venue's change history.",
    icon: MapPinned,
    testId: "admin-section-venues",
  },
  // Issue #1191 (D234): the lookup for the privacy-safe failed-request record.
  {
    to: "/admin/errors",
    label: "Errors",
    description: "Look up failed requests from the last 14 days: route, status, code and request id.",
    icon: TriangleAlert,
    testId: "admin-section-errors",
  },
];

export default function AdminHome() {
  return (
    <div className="space-y-6" data-testid="admin-home">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-500">Admin</p>
        <h1 className="font-display font-black text-2xl text-slate-900 mt-1">Admin</h1>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {SECTIONS.map((s) => (
          <Link key={s.to} to={s.to} data-testid={s.testId}>
            <Card className="border-slate-200 hover:shadow-md hover:-translate-y-0.5 transition-all h-full">
              <CardContent className="p-5 flex items-center gap-4">
                <div className="w-11 h-11 rounded-lg flex items-center justify-center shrink-0" style={{ background: "#0B2E5915" }}>
                  <s.icon size={20} color="#0B2E59" />
                </div>
                <div className="min-w-0 flex-1">
                  <div className="font-display font-bold text-slate-900">{s.label}</div>
                  <div className="text-xs text-slate-500 mt-0.5">{s.description}</div>
                </div>
                <ChevronRight size={16} className="text-slate-400 shrink-0" />
              </CardContent>
            </Card>
          </Link>
        ))}
      </div>

      {/* #1151: Grounds (the players' directory) left the admin menu. This is
          the admin's labelled way back in, archived rows included. */}
      <Link
        to="/grounds-map"
        data-testid="admin-directory-link"
        className="inline-flex items-center gap-1 text-sm text-slate-600 underline underline-offset-2 hover:text-slate-900"
      >
        Open the venue directory as players see it, archived venues included
        <ChevronRight size={14} />
      </Link>
    </div>
  );
}
